import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem as MuiMenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AppIconButton } from '../ui';
import CollapsibleFilters from '../ui/CollapsibleFilters';
import { inventoryApi } from '../../api/inventoryApi';
import { formatQuantity } from '../../utils/formatQuantity';
import type { ThemeColors } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import type { PlannerMenuItemOption } from './PlannerTab';

interface SaleLine {
  menuItemId: number;
  menuItemName: string;
  portionId: number | null;
  portionName: string | null;
  quantity: number;
}

interface ManualSaleResponseLine {
  menuItemId: number;
  menuItemName: string;
  portionId: number | null;
  portionName: string | null;
  quantity: number;
}

interface ManualSaleShortfall {
  inventoryItemId: number;
  name: string;
  unit: string;
  expected: number;
  deducted: number;
}

interface ManualSaleResponse {
  id: number;
  saleDate: string; // yyyy-MM-dd
  notes: string | null;
  stockShortfall: boolean;
  createdBy: string | null;
  updatedAt: string | null;
  lines: ManualSaleResponseLine[];
  /** per-ingredient detail, present (non-empty) only when stockShortfall is true */
  shortfalls?: ManualSaleShortfall[];
}

interface ApiError {
  response?: { data?: { error?: string; message?: string } };
}

interface DailySalesTabProps {
  menuItems: PlannerMenuItemOption[];
  theme: ThemeColors;
  readOnly?: boolean;
  /** open the full variance report for the given day (yyyy-MM-dd) */
  onViewVariance?: (date: string) => void;
}

/** round to at most 2 decimals (used for the raw base-unit record-actual input) */
const round2 = (n: number): number => Math.round(n * 100) / 100;

const todayIso = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const daysAgoIso = (days: number): string => {
  const now = new Date();
  now.setDate(now.getDate() - days);
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

/** yyyy-MM-dd -> "Mon, 6 Jul 2026" (no time-zone shift: parse the parts directly). */
const formatDayLabel = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Daily sales reporting for standalone-mode vendors (no POS). The owner records
 * what sold each day; the backend deducts ingredient stock through recipes.
 */
const DailySalesTab: React.FC<DailySalesTabProps> = ({ menuItems, theme, readOnly = false, onViewVariance }) => {
  const confirm = useConfirm();
  const [days, setDays] = useState<ManualSaleResponse[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const [saleDate, setSaleDate] = useState<string>(todayIso());
  const [lines, setLines] = useState<SaleLine[]>([]);
  const [notes, setNotes] = useState('');
  const [newItemId, setNewItemId] = useState<number | ''>('');
  const [newPortionId, setNewPortionId] = useState<number | ''>('');
  const [newQuantity, setNewQuantity] = useState<number>(0);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Inline "record actual usage" per shortfall row, keyed by `${dayId}-${itemId}`.
  const [recordingKey, setRecordingKey] = useState<string | null>(null);
  const [recordValue, setRecordValue] = useState<number>(0);
  const [recordingBusy, setRecordingBusy] = useState(false);

  // Reported-days date-range filter: defaults to the last 30 days.
  const defaultFrom = useMemo(() => daysAgoIso(30), []);
  const defaultTo = useMemo(() => todayIso(), []);
  const [filterFrom, setFilterFrom] = useState<string>(defaultFrom);
  const [filterTo, setFilterTo] = useState<string>(defaultTo);
  const activeFilterCount =
    (filterFrom !== defaultFrom ? 1 : 0) + (filterTo !== defaultTo ? 1 : 0);

  const selectedItem = menuItems.find((mi) => mi.id === newItemId);

  const loadDays = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await inventoryApi.getManualSales(filterFrom, filterTo);
      const data: ManualSaleResponse[] = res.data || [];
      data.sort((a, b) => b.saleDate.localeCompare(a.saleDate));
      setDays(data);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to load reported days');
    } finally {
      setListLoading(false);
    }
  }, [filterFrom, filterTo]);

  const clearFilters = () => {
    setFilterFrom(defaultFrom);
    setFilterTo(defaultTo);
  };

  useEffect(() => {
    loadDays();
  }, [loadDays]);

  const resetForm = () => {
    setEditingId(null);
    setSaleDate(todayIso());
    setLines([]);
    setNotes('');
    setNewItemId('');
    setNewPortionId('');
    setNewQuantity(0);
  };

  const handleAddLine = () => {
    if (!newItemId || newQuantity <= 0 || !selectedItem) return;
    const portionId = newPortionId === '' ? null : (newPortionId as number);
    const portionName = portionId
      ? selectedItem.portions?.find((p) => p.id === portionId)?.portionName || null
      : null;

    setLines((prev) => {
      const existing = prev.find((l) => l.menuItemId === newItemId && l.portionId === portionId);
      if (existing) {
        return prev.map((l) => (l === existing ? { ...l, quantity: l.quantity + newQuantity } : l));
      }
      return [
        ...prev,
        { menuItemId: newItemId as number, menuItemName: selectedItem.name, portionId, portionName, quantity: newQuantity },
      ];
    });
    setNewItemId('');
    setNewPortionId('');
    setNewQuantity(0);
  };

  const handleRemoveLine = (line: SaleLine) => {
    setLines((prev) => prev.filter((l) => l !== line));
  };

  const handleSave = async () => {
    if (lines.length === 0) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    const payload = {
      saleDate,
      notes: notes.trim() || undefined,
      lines: lines.map((l) => ({ menuItemId: l.menuItemId, portionId: l.portionId, quantity: l.quantity })),
    };
    try {
      if (editingId != null) {
        await inventoryApi.updateManualSale(editingId, payload);
        setSuccess("Day's sales updated. Stock has been recalculated.");
      } else {
        await inventoryApi.createManualSale(payload);
        setSuccess("Day's sales saved. Ingredient stock has been deducted.");
      }
      resetForm();
      await loadDays();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || "Failed to save the day's sales");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (day: ManualSaleResponse) => {
    setEditingId(day.id);
    setSaleDate(day.saleDate);
    setNotes(day.notes || '');
    setLines(
      day.lines.map((l) => ({
        menuItemId: l.menuItemId,
        menuItemName: l.menuItemName,
        portionId: l.portionId,
        portionName: l.portionName,
        quantity: l.quantity,
      }))
    );
    setNewItemId('');
    setNewPortionId('');
    setNewQuantity(0);
    setError(null);
    setSuccess(null);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (day: ManualSaleResponse) => {
    if (!(await confirm({
      title: 'Remove sale entry',
      description: `Delete the sales reported for ${formatDayLabel(day.saleDate)}? This reverses the stock deduction.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    }))) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await inventoryApi.deleteManualSale(day.id);
      if (editingId === day.id) resetForm();
      setSuccess("Day's sales deleted. Stock deduction reversed.");
      await loadDays();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || "Failed to delete the day's sales");
    }
  };

  const startRecording = (day: ManualSaleResponse, s: ManualSaleShortfall) => {
    setRecordingKey(`${day.id}-${s.inventoryItemId}`);
    // prefill with the remaining gap in the ingredient's raw base unit
    setRecordValue(round2(Math.max(s.expected - s.deducted, 0)));
  };

  const cancelRecording = () => {
    setRecordingKey(null);
    setRecordValue(0);
  };

  const confirmRecording = async (day: ManualSaleResponse, s: ManualSaleShortfall) => {
    setRecordingBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await inventoryApi.recordManualSaleUsage(day.id, {
        inventoryItemId: s.inventoryItemId,
        quantity: recordValue,
      });
      const updated = res.data as ManualSaleResponse;
      setDays((prev) => prev.map((d) => (d.id === day.id ? updated : d)));
      setSuccess('Actual usage recorded');
      cancelRecording();
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to record actual usage');
    } finally {
      setRecordingBusy(false);
    }
  };

  const lineLabel = (line: SaleLine) =>
    line.portionName ? `${line.menuItemName} — ${line.portionName}` : line.menuItemName;

  const dayItemCount = (day: ManualSaleResponse) => day.lines.reduce((sum, l) => sum + l.quantity, 0);

  const cardSx = {
    backgroundColor: theme.components?.card?.background,
    border: `1px solid ${theme.components?.card?.border}`,
    borderRadius: 2,
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}
      {menuItems.length === 0 && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No menu items found. Create your menu first (sidebar → Menu) to report sales.
        </Alert>
      )}

      {/* Entry form (hidden in read-only mode) */}
      {!readOnly && (
        <Card sx={{ ...cardSx, mb: 3 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.95rem', fontWeight: 600, color: theme.text, mb: 0.5 }}>
              {editingId != null ? "Edit reported sales" : "Report today's sales"}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.813rem', color: theme.gray?.[600], mb: 2 }}>
              Add the dishes that sold and save the day — ingredient stock is deducted automatically
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Sale date"
                  type="date"
                  size="small"
                  fullWidth
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  disabled={editingId != null}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayIso(), 'data-testid': 'daily-sales-date' }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Menu Item</InputLabel>
                  <Select
                    value={newItemId}
                    onChange={(e) => { setNewItemId(Number(e.target.value)); setNewPortionId(''); }}
                    label="Menu Item"
                    data-testid="daily-sales-item-select"
                  >
                    {menuItems.map((mi) => (
                      <MuiMenuItem key={mi.id} value={mi.id}>
                        {mi.name} {mi.categoryName ? `(${mi.categoryName})` : ''}
                      </MuiMenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {selectedItem?.portions && selectedItem.portions.length > 0 && (
                <Grid item xs={6} sm={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Portion</InputLabel>
                    <Select
                      value={newPortionId}
                      onChange={(e) => setNewPortionId(e.target.value === '' ? '' : Number(e.target.value))}
                      label="Portion"
                      data-testid="daily-sales-portion-select"
                    >
                      <MuiMenuItem value="">Standard</MuiMenuItem>
                      {selectedItem.portions.map((p) => (
                        <MuiMenuItem key={p.id} value={p.id}>{p.portionName}</MuiMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Sold"
                  type="number"
                  size="small"
                  fullWidth
                  value={newQuantity || ''}
                  onChange={(e) => setNewQuantity(parseInt(e.target.value, 10) || 0)}
                  inputProps={{ min: 1, step: 1, 'data-testid': 'daily-sales-qty' }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  startIcon={<PlusIcon className="h-4 w-4" />}
                  onClick={handleAddLine}
                  disabled={!newItemId || newQuantity <= 0}
                  data-testid="daily-sales-add-line"
                  sx={{
                    backgroundColor: `${theme.button?.secondary?.bg || theme.background} !important`,
                    color: `${theme.primary} !important`,
                    border: `1px solid ${theme.primary} !important`,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none !important',
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            {/* Added lines */}
            {lines.length > 0 && (
              <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {lines.map((line) => (
                  <Box
                    key={`${line.menuItemId}-${line.portionId ?? 'base'}`}
                    data-testid="daily-sales-line"
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.5, py: 1, borderRadius: 1.5,
                      backgroundColor: theme.background,
                      border: `1px solid ${theme.components?.card?.border}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.813rem', fontWeight: 500, color: theme.text }}>
                      {lineLabel(line)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.813rem', fontWeight: 700, color: theme.text }}>
                        × {line.quantity}
                      </Typography>
                      <AppIconButton
                        variant="danger"
                        tooltip="Remove"
                        size="small"
                        icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                        onClick={() => handleRemoveLine(line)}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {editingId != null && (
              <Typography variant="caption" sx={{ display: 'block', mt: 2.5, color: theme.gray?.[600] }}>
                Updating recalculates stock: previous deduction is reversed, then re-applied.
              </Typography>
            )}

            {/* Notes + Save share a single row; wrap on mobile */}
            <Box sx={{ mt: editingId != null ? 1 : 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                label="Notes (optional)"
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                inputProps={{ 'data-testid': 'daily-sales-notes' }}
                sx={{ flex: 1, minWidth: 200 }}
              />
              {editingId != null && (
                <Button
                  variant="text"
                  onClick={resetForm}
                  disabled={saving}
                  sx={{ color: theme.gray?.[600], textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={lines.length === 0 || saving}
                startIcon={saving ? <CircularProgress size={16} /> : undefined}
                data-testid="daily-sales-save"
                sx={{
                  backgroundColor: `${theme.primary} !important`,
                  color: `${theme.button?.primary?.text || '#fff'} !important`,
                  border: `1px solid ${theme.primary} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  whiteSpace: 'nowrap',
                  '&:disabled': { opacity: 0.5 },
                }}
              >
                {editingId != null ? "Update day's sales" : "Save day's sales"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Reported days */}
      <Typography variant="subtitle1" sx={{ fontSize: '0.95rem', fontWeight: 600, color: theme.text, mb: 1.5 }}>
        Reported days
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <CollapsibleFilters
          theme={theme}
          activeCount={activeFilterCount}
          onClear={clearFilters}
          storageKey="inventory_daily_sales_filters_open"
        >
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: filterTo || todayIso(), 'data-testid': 'daily-sales-filter-from' }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filterFrom || undefined, max: todayIso(), 'data-testid': 'daily-sales-filter-to' }}
              />
            </Grid>
          </Grid>
        </CollapsibleFilters>
      </Box>

      {listLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: theme.primary }} />
        </Box>
      ) : days.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: '0.813rem', color: theme.gray?.[600], py: 3, textAlign: 'center' }}>
              No sales reported yet.{' '}
              {readOnly ? 'Reported days will appear here.' : 'Report today’s sales above to start tracking stock.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {days.map((day) => {
            const expanded = expandedId === day.id;
            return (
              <Card key={day.id} data-testid="daily-sales-day-card" sx={cardSx}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', cursor: 'pointer', flex: 1, minWidth: 0 }}
                      onClick={() => setExpandedId(expanded ? null : day.id)}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.813rem', fontWeight: 600, color: theme.text }}>
                        {formatDayLabel(day.saleDate)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: theme.gray?.[600] }}>
                        {dayItemCount(day)} item{dayItemCount(day) === 1 ? '' : 's'} sold
                      </Typography>
                      {day.stockShortfall && (
                        <Chip
                          size="small"
                          data-testid="daily-sales-shortfall-chip"
                          icon={<ExclamationTriangleIcon style={{ width: 14, height: 14 }} />}
                          label="Stock ran short"
                          sx={{
                            height: 22,
                            color: theme.warning,
                            backgroundColor: `${theme.warning}18`,
                            border: `1px solid ${theme.warning}`,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: theme.warning },
                          }}
                        />
                      )}
                      {day.stockShortfall && onViewVariance && (
                        <Button
                          variant="text"
                          size="small"
                          data-testid="daily-sales-variance-link"
                          onClick={(e) => { e.stopPropagation(); onViewVariance(day.saleDate); }}
                          sx={{
                            minWidth: 0,
                            p: 0,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            color: theme.primary,
                            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                          }}
                        >
                          View variance →
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {!readOnly && (
                        <>
                          <AppIconButton
                            tooltip="Edit"
                            size="small"
                            data-testid="daily-sales-edit"
                            icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />}
                            onClick={() => handleEdit(day)}
                          />
                          <AppIconButton
                            variant="danger"
                            tooltip="Delete"
                            size="small"
                            data-testid="daily-sales-delete"
                            icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                            onClick={() => handleDelete(day)}
                          />
                        </>
                      )}
                      <AppIconButton
                        tooltip={expanded ? 'Collapse' : 'Expand'}
                        size="small"
                        icon={expanded
                          ? <ChevronUpIcon style={{ width: 16, height: 16 }} />
                          : <ChevronDownIcon style={{ width: 16, height: 16 }} />}
                        onClick={() => setExpandedId(expanded ? null : day.id)}
                      />
                    </Box>
                  </Box>

                  <Collapse in={expanded} unmountOnExit>
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {day.lines.map((l) => (
                        <Box
                          key={`${l.menuItemId}-${l.portionId ?? 'base'}`}
                          sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5, borderRadius: 1, backgroundColor: theme.background }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.813rem', color: theme.text }}>
                            {l.portionName ? `${l.menuItemName} — ${l.portionName}` : l.menuItemName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.813rem', fontWeight: 700, color: theme.text }}>
                            × {l.quantity}
                          </Typography>
                        </Box>
                      ))}
                      {day.shortfalls && day.shortfalls.length > 0 && (
                        <Box
                          data-testid="daily-sales-shortfall-detail"
                          sx={{ mt: 1, p: 1, borderRadius: 1, border: `1px solid ${theme.warning}`, backgroundColor: `${theme.warning}10` }}
                        >
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: theme.gray?.[600], mb: 0.5 }}>
                            Stock gave less than recipes needed:
                          </Typography>
                          {day.shortfalls.map((s) => {
                            const isRecording = recordingKey === `${day.id}-${s.inventoryItemId}`;
                            return (
                              <Box
                                key={s.inventoryItemId}
                                data-testid="daily-sales-shortfall-row"
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.25, flexWrap: 'wrap' }}
                              >
                                <Typography variant="body2" sx={{ fontSize: '0.813rem', color: theme.text }}>
                                  {s.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.813rem', color: theme.text }}>
                                    {s.deducted === 0 ? (
                                      <>{formatQuantity(s.expected, s.unit)} (estimate)</>
                                    ) : (
                                      <>
                                        <Box component="span" sx={{ color: theme.warning, fontWeight: 700 }}>
                                          {formatQuantity(s.deducted, s.unit)}
                                        </Box>
                                        {' '}of {formatQuantity(s.expected, s.unit)}
                                      </>
                                    )}
                                  </Typography>
                                  {!readOnly && !isRecording && (
                                    <Button
                                      variant="text"
                                      size="small"
                                      data-testid="daily-sales-record-actual"
                                      onClick={() => startRecording(day, s)}
                                      sx={{
                                        minWidth: 0,
                                        p: 0,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: theme.primary,
                                        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                                      }}
                                    >
                                      Record actual
                                    </Button>
                                  )}
                                  {!readOnly && isRecording && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <TextField
                                        label={s.unit}
                                        type="number"
                                        size="small"
                                        value={recordValue || ''}
                                        onChange={(e) => setRecordValue(parseFloat(e.target.value) || 0)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 110 }}
                                        inputProps={{ min: 0, step: 'any', 'data-testid': 'daily-sales-record-input' }}
                                      />
                                      <AppIconButton
                                        tooltip="Confirm"
                                        size="small"
                                        data-testid="daily-sales-record-confirm"
                                        disabled={recordingBusy || recordValue <= 0}
                                        icon={<CheckIcon style={{ width: 16, height: 16 }} />}
                                        onClick={() => confirmRecording(day, s)}
                                      />
                                      <AppIconButton
                                        variant="danger"
                                        tooltip="Cancel"
                                        size="small"
                                        data-testid="daily-sales-record-cancel"
                                        disabled={recordingBusy}
                                        icon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                                        onClick={cancelRecording}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {day.notes && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: theme.gray?.[600], mt: 0.5 }}>
                          {day.notes}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default DailySalesTab;
